export type ReasoningPhase = "idle" | "streaming" | "done";

export interface ReasoningPhaseState {
  phase: ReasoningPhase;
  /** Milliseconds elapsed from first reasoning chunk to first delta chunk. */
  elapsedMs: number;
}

/**
 * Tracks the lifecycle of the reasoning phase for the *current* streaming message.
 *
 * State transitions:
 *   idle → streaming  (first reasoning chunk arrives)
 *   streaming → done  (first delta/content chunk arrives, or stream ends)
 *   done → idle       (reset at start of next message stream)
 *
 * Consumers gate on `isStreaming` from the message context so historical
 * messages always render as "done" regardless of the singleton state.
 */
class ReasoningPhaseStore {
  private state: ReasoningPhaseState = { phase: "idle", elapsedMs: 0 };
  private startMs = 0;
  private listeners = new Set<() => void>();

  /** Called when a new message stream begins (before any chunks). */
  reset(): void {
    this.startMs = 0;
    this.setState({ phase: "idle", elapsedMs: 0 });
  }

  /** Called when the first non-empty reasoning chunk arrives. */
  setStreaming(): void {
    if (this.state.phase !== "idle") return;
    this.startMs = Date.now();
    this.setState({ phase: "streaming", elapsedMs: 0 });
  }

  /**
   * Called when the first content delta arrives (reasoning phase ended)
   * or when the stream finishes with only reasoning and no delta.
   */
  setDone(): void {
    if (this.state.phase !== "streaming") return;
    const elapsedMs = this.startMs > 0 ? Date.now() - this.startMs : 0;
    this.setState({ phase: "done", elapsedMs });
  }

  getSnapshot(): ReasoningPhaseState {
    return this.state;
  }

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private setState(s: ReasoningPhaseState): void {
    this.state = s;
    this.listeners.forEach((l) => l());
  }
}

export const reasoningPhaseStore = new ReasoningPhaseStore();
